import { debounce } from 'debounce-promise-with-cancel';
import { useEffect, useRef } from 'react';
import { type AsyncState, useAsync } from 'react-async';
import useConstant from 'use-constant';

/*
 * Compose useAsync and debounce-promise-with-cancel into a cancelable debounce hook.
 * with all the bells and whistles of react-async such as status reporting.
 * Callback function should be created with useCallback.
 */
export const useDebouncedChangeHandler = (
  fn: ((...args: any[]) => Promise<void>) | ((...args: any[]) => void),
  ms: number
): AsyncState<void> => {
  const callbackRef = useRef(fn);
  callbackRef.current = fn;
  const debouncedFn = useConstant(() => debounce(async (args: any[]) => callbackRef.current(...args), ms));

  const asyncValue = useAsync({
    deferFn: debouncedFn,
    onCancel: debouncedFn.cancel
  });

  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return asyncValue;
};
